import React, { useState } from "react";
import {
  PlayCircle,
  X,
  Phone,
  ArrowSquareOut,
  BookOpen,
} from "@phosphor-icons/react";

// YouTube thumbnails are served free by Google — we never upload or store them.
const ytId = (url) => {
  const m = url.match(/(?:youtu\.be\/|v=)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : "";
};
const ytThumb = (id) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`;

const VIDEOS = [
  {
    url: "https://youtu.be/qxQBc9UPVLI",
    title: "Market Profile · session highlight",
  },
  {
    url: "https://youtu.be/_5ihifIyCH8",
    title: "Order Flow in action",
  },
  {
    url: "https://youtu.be/dYj7MTXPxL8",
    title: "Live trade walkthrough",
  },
  {
    url: "https://youtu.be/CY5jB6zkHsE",
    title: "Reading auction context",
  },
  {
    url: "https://youtu.be/srRQhU1IjzY",
    title: "Trade plan · setup breakdown",
  },
  {
    url: "https://youtu.be/A-XQXPR0Wtw",
    title: "Live market commentary",
  },
];

const BROKERS = [
  {
    name: "5paisa",
    logo: "/5paisa-logo.png",
    blurb:
      "Low-brokerage demat & trading account. Open through this link to claim our community benefits.",
    href:
      "https://www.5paisa.com/demat-account?ReferralCode=54695526&ReturnUrl=invest-open-account",
    cta: "Open 5paisa account",
    testId: "broker-5paisa",
  },
  {
    name: "Alice Blue",
    logo: "/aliceblue-logo.png",
    blurb:
      "Free equity delivery + low intraday brokerage. Use this link to be tagged to our partner code.",
    href: "https://ekyc.aliceblueonline.com/?source=SKLC27",
    cta: "Open Alice Blue account",
    testId: "broker-aliceblue",
  },
];

const HELPLINE = "7356326453";
const HELPLINE_DISPLAY = "+91 73563 26453";

export default function Resources() {
  const [active, setActive] = useState(null); // active video id

  return (
    <section
      id="resources"
      data-testid="resources-section"
      className="bg-[#F4F3ED] text-[#0A0A0A] px-5 lg:px-10 py-20 lg:py-28 border-t border-[#D4D0C5]"
    >
      <div className="max-w-[1320px] mx-auto">
        <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-3">
          RESOURCES
        </div>
        <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-3">
          Watch. Learn. Open an account.
        </h2>
        <p className="font-body text-base lg:text-lg text-[#4A4A4A] max-w-2xl mb-12">
          Session highlights from Vishnu Sir, plus the brokers we recommend for
          live trading with our community.
        </p>

        {/* ---------- Videos ---------- */}
        <div className="mb-16">
          <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#0A0A0A] mb-5">
            01 · Watch
          </div>
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-5"
            data-testid="resources-videos"
          >
            {VIDEOS.map((v, i) => {
              const id = ytId(v.url);
              return (
                <button
                  key={id || i}
                  type="button"
                  data-testid={`video-card-${i}`}
                  onClick={() => setActive(id)}
                  className="group text-left border border-[#0A0A0A] bg-[#F4F3ED] hover:bg-[#0A0A0A] hover:text-[#F4F3ED] transition-colors"
                >
                  <div className="relative aspect-video overflow-hidden bg-[#0A0A0A]">
                    {id ? (
                      <img
                        src={ytThumb(id)}
                        alt={v.title}
                        loading="lazy"
                        className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                      />
                    ) : null}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <PlayCircle
                        size={56}
                        weight="fill"
                        className="text-[#F4F3ED] drop-shadow-lg group-hover:text-[#D9381E] transition-colors"
                      />
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#D9381E] mb-1.5">
                      YouTube · Great Eagle Tech
                    </div>
                    <div className="font-display font-bold text-base tracking-tight">
                      {v.title}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <a
            href="https://www.youtube.com/@mallumarketprofile"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="resources-youtube-channel"
            className="inline-flex items-center gap-2 mt-6 font-mono-acc text-[11px] tracking-[0.25em] uppercase border-b border-[#0A0A0A] pb-1 hover:text-[#D9381E] hover:border-[#D9381E] transition-colors"
          >
            See full channel <ArrowSquareOut size={14} weight="bold" />
          </a>
        </div>

        {/* ---------- Brokers ---------- */}
        <div>
          <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#0A0A0A] mb-5">
            02 · Open your trading account
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
            {BROKERS.map((b) => (
              <div
                key={b.name}
                data-testid={b.testId}
                className="border border-[#0A0A0A] bg-[#F4F3ED] p-7 flex flex-col"
              >
                <div className="flex items-center justify-between gap-4 mb-5">
                  <div className="flex items-center gap-2">
                    <BookOpen
                      size={18}
                      weight="duotone"
                      className="text-[#D9381E]"
                    />
                    <div className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A]">
                      Recommended broker
                    </div>
                  </div>
                  <div className="h-10 w-24 bg-white border border-[#D4D0C5] flex items-center justify-center px-2 shrink-0">
                    <img
                      src={b.logo}
                      alt={`${b.name} logo`}
                      data-testid={`${b.testId}-logo`}
                      className="max-h-7 max-w-full object-contain"
                      loading="lazy"
                    />
                  </div>
                </div>
                <h3 className="font-display font-black text-2xl lg:text-3xl tracking-tighter mb-2">
                  {b.name}
                </h3>
                <p className="font-body text-sm text-[#4A4A4A] leading-relaxed mb-6 flex-1">
                  {b.blurb}
                </p>
                <a
                  href={b.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${b.testId}-link`}
                  className="inline-flex items-center justify-center gap-3 bg-[#0A0A0A] text-[#F4F3ED] py-3.5 font-body text-sm uppercase tracking-[0.22em] hover:bg-[#D9381E] transition-colors"
                >
                  {b.cta} <ArrowSquareOut size={16} weight="bold" />
                </a>
              </div>
            ))}
          </div>

          <div
            className="border border-[#D4D0C5] bg-[#EAE8DF] p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
            data-testid="resources-helpline"
          >
            <div>
              <div className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#D9381E] mb-1">
                For more details
              </div>
              <div className="font-display font-bold text-base">
                Call our account-opening helpline
              </div>
            </div>
            <a
              href={`tel:+91${HELPLINE}`}
              data-testid="resources-helpline-link"
              className="inline-flex items-center gap-2 bg-[#0A0A0A] text-[#F4F3ED] px-5 py-3 font-mono-acc text-sm tracking-[0.18em] hover:bg-[#D9381E] transition-colors"
            >
              <Phone size={16} weight="fill" /> {HELPLINE_DISPLAY}
            </a>
          </div>
        </div>
      </div>

      {/* ---------- Lightbox ---------- */}
      {active && (
        <div
          data-testid="video-lightbox"
          className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setActive(null)}
        >
          <div
            className="relative w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setActive(null)}
              data-testid="video-lightbox-close"
              aria-label="Close"
              className="absolute -top-12 right-0 inline-flex items-center gap-2 font-mono-acc text-[11px] tracking-[0.25em] uppercase text-white hover:text-[#D9381E]"
            >
              Close <X size={16} weight="bold" />
            </button>
            <div className="aspect-video w-full bg-black">
              <iframe
                title="YouTube video"
                src={`https://www.youtube.com/embed/${active}?autoplay=1&rel=0`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
